import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import {
  ChooseColumnDialogPhases,
  SelectableCustomColumn,
  DEFAULT_PRODUCT_DATA_TYPE,
  DEFAULT_VENDOR_DATA_TYPE,
  DEFAULT_PRODUCT_COLUMN_NAME,
  DEFAULT_VENDOR_COLUMN_NAME,
  ProductPresenceDataType,
  VendorPresenceDataType,
} from "./types";
import { ColumnTypeStep } from "./steps/ColumnTypeStep";
import { ProductConfigStep } from "./steps/ProductConfigStep";
import { VendorConfigStep } from "./steps/VendorConfigStep";

interface ChooseColumnDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (column: Omit<SelectableCustomColumn, "id">) => void;
  editingColumn?: SelectableCustomColumn | null;
}

const steps = ["Choose Type", "Configure Column"];

export const ChooseColumnDialog: React.FC<ChooseColumnDialogProps> = ({
  open,
  onClose,
  onSave,
  editingColumn,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [columnType, setColumnType] = useState<"product" | "vendor" | null>(
    null
  );
  const [columnName, setColumnName] = useState("");
  const [dataType, setDataType] = useState<
    ProductPresenceDataType | VendorPresenceDataType | ""
  >("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    if (editingColumn) {
      setColumnType(editingColumn.type);
      setColumnName(editingColumn.name);
      setDataType(editingColumn.dataType);
      setSelectedIds(editingColumn.ids);
      setActiveStep(1); // Skip to configuration step when editing
    } else {
      // Reset form for new column
      setColumnType(null);
      setColumnName("");
      setDataType("");
      setSelectedIds([]);
      setActiveStep(0);
    }
  }, [editingColumn, open]);

  const handleNext = () => {
    if (activeStep === 0 && columnType) {
      // Set default values when moving to configuration step
      if (columnType === "product") {
        setColumnName(columnName || DEFAULT_PRODUCT_COLUMN_NAME);
        setDataType(dataType || DEFAULT_PRODUCT_DATA_TYPE);
      } else {
        setColumnName(columnName || DEFAULT_VENDOR_COLUMN_NAME);
        setDataType(dataType || DEFAULT_VENDOR_DATA_TYPE);
      }
      setActiveStep(1);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handleDataTypeChange = (newDataType: string) => {
    setDataType(
      newDataType as ProductPresenceDataType | VendorPresenceDataType
    );
  };

  const handleSave = () => {
    if (columnType && columnName && dataType && selectedIds.length > 0) {
      onSave({
        type: columnType,
        name: columnName,
        dataType: dataType as ProductPresenceDataType | VendorPresenceDataType,
        ids: selectedIds,
        isSelected: true,
        phase: ChooseColumnDialogPhases.CHOOSE_TYPE,
      });
    }
  };

  const handleClose = () => {
    onClose();
  };

  const isStepValid = () => {
    if (activeStep === 0) {
      return columnType !== null;
    }
    if (activeStep === 1) {
      return (
        columnName.trim() !== "" && dataType !== "" && selectedIds.length > 0
      );
    }
    return false;
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <ColumnTypeStep
            selectedType={columnType}
            onTypeSelect={setColumnType}
          />
        );
      case 1:
        if (columnType === "product") {
          return (
            <ProductConfigStep
              columnName={columnName}
              dataType={dataType}
              selectedIds={selectedIds}
              onColumnNameChange={setColumnName}
              onDataTypeChange={handleDataTypeChange}
              onSelectedIdsChange={setSelectedIds}
            />
          );
        } else if (columnType === "vendor") {
          return (
            <VendorConfigStep
              columnName={columnName}
              dataType={dataType}
              selectedIds={selectedIds}
              onColumnNameChange={setColumnName}
              onDataTypeChange={handleDataTypeChange}
              onSelectedIdsChange={setSelectedIds}
            />
          );
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { minHeight: 500 },
        },
      }}
    >
      <DialogTitle>
        {editingColumn ? "Edit Custom Column" : "Create Custom Column"}
      </DialogTitle>

      <DialogContent>
        {!editingColumn && (
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {getStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {activeStep > 0 && !editingColumn && (
          <Button onClick={handleBack}>Back</Button>
        )}
        {activeStep === 0 && !editingColumn ? (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={!isStepValid()}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isStepValid()}
          >
            {editingColumn ? "Update" : "Create"} Column
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
